import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import clsx from "clsx";

interface ScorecardDataTableProps {
  isNineHoles?: boolean;
  scoreCardData: any;
  isLoading: boolean;
  isFront: boolean;
  currentHoleNumber: number;
  currentShotNumber: number;
  setCurrentShotNumber: (currentShot: number) => void;
  setCurrentHoleNumber: (currentShot: number) => void;
}

interface HoleData {
  grossScore: number;
  par: number;
  holeNumber: number;
  shots: [];
  distance: number;
}

const ScorecardDataTable = ({
  isNineHoles,
  scoreCardData,
  isLoading,
  isFront,
  currentShotNumber,
  currentHoleNumber,
  setCurrentHoleNumber,
  setCurrentShotNumber,
}: ScorecardDataTableProps) => {
  const handleHoleClick = ({ holeNumber }: { holeNumber: number }) => {
    setCurrentHoleNumber(holeNumber);
    if (currentShotNumber !== 1) {
      setCurrentShotNumber(1);
    }
  };

  const isNineHoleRound =
    isNineHoles || scoreCardData?.data?.data?.node?.holes?.length === 9;

  let holeStartIndex = 0;
  let holeEndIndex = 9;

  if (!isFront && !isNineHoleRound) {
    holeStartIndex = 9;
    holeEndIndex = 18;
  }

  let yards = 0;
  let par = 0;
  let score = 0;
  let totalYards = 0;
  let totalPar = 0;
  let totalScore = 0;

  scoreCardData?.data?.data?.node?.holes.forEach(
    (hole: HoleData, index: number) => {
      if (isFront && index < 9) {
        yards += Math.floor(hole.distance * 1.09361);
        par += hole.par;
        score += hole.grossScore;
      }
      if (!isFront && index >= 9) {
        yards += Math.floor(hole.distance * 1.09361);
        par += hole.par;
        score += hole.grossScore;
      }
      totalYards += Math.floor(hole.distance * 1.09361);
      totalPar += hole.par;
      totalScore += hole.grossScore;
    }
  );

  return (
    <div
      className={clsx("flex text-[10px] min-[441px]:!text-xs md:!text-[15px]", {
        "rounded-[10px] overflow-hidden min-[1311px]:!rounded-none min-[1311px]:!rounded-l-[10px]":
          !isLoading && isFront,
        "rounded-[10px] overflow-hidden min-[1311px]:!rounded-none min-[1311px]:!rounded-r-[10px]":
          !isLoading && !isFront,
        "min-[1311px]:!rounded-[10px]":
          (isFront && isNineHoleRound) || (!isFront && isNineHoleRound),
      })}
    >
      <div
        className={clsx(
          `flex flex-col justify-between text-center w-full transition-colors duration-300
          last-of-type:hover:bg-transparent last-of-type:hover:cursor-default
          [&>*]:flex [&>*]:items-center [&>*]:justify-center
          [&>*]:min-h-8 [&>*]:first-child:h-[40px] md:[&>*]:min-h-[40px] md:[&>*]:first-child:h-[56px] lg:[&>*]:min-h-[44px]
          [&>*]:last-of-type:bg-[rgba(118,118,118,0.5)]
          cursor-default bg-[rgba(118,118,118,0.5)] hover:bg-[rgba(118,118,118,0.5)]
          `,
          { "min-[1311px]:!hidden": !isFront }
        )}
      >
        <div>Hole</div>
        <div>Yds</div>
        <div>Par</div>
        <div>Total</div>
      </div>
      {isLoading &&
        Array.from(Array(9)).map((_item: any, i: number) => (
          <div
            className={`flex flex-col justify-between text-center w-full cursor-pointer transition-colors duration-300 
              hover:bg-[rgba(236,105,26,0.4)]
              last-of-type:hover:bg-transparent last-of-type:hover:cursor-default
              [&>*]:flex [&>*]:items-center [&>*]:justify-center
              [&>*]:min-h-8 [&>*]:first-child:h-[40px] md:[&>*]:min-h-[40px] md:[&>*]:first-child:h-[56px] lg:[&>*]:min-h-[44px]
              [&>*]:last-of-type:bg-[rgba(118,118,118,0.5)]
            `}
            key={i}
          >
            <div>
              <span className="inline-block h-[15px] w-5 bg-tm-c-gray-950 opacity-60 rounded-[10px]" />
            </div>
            <div>
              <span className="inline-block h-[15px] w-5 bg-tm-c-gray-950 opacity-60 rounded-[10px]" />
            </div>
            <div>
              <span className="inline-block h-[15px] w-5 bg-tm-c-gray-950 opacity-60 rounded-[10px]" />
            </div>
            <div>
              <span>
                <span>
                  <span className="inline-block h-[15px] w-5 bg-tm-c-gray-950 opacity-60 rounded-[10px]" />
                </span>
              </span>
            </div>
          </div>
        ))}
      {scoreCardData?.data?.data?.node?.holes
        ?.slice(holeStartIndex, holeEndIndex)
        ?.map((hole: HoleData) => {
          const renderLegendClasses = clsx(
            // base legend
            "flex items-center justify-center w-[18px] h-[18px] leading-normal m-0 p-0",
            "md:w-[20px] md:h-[20px]",
            {
              // birdie
              "rounded-full bg-tm-c-brand":
                hole.grossScore && hole.grossScore === hole.par - 1,
              //eagle
              "rounded-full border border-tm-c-light overflow-hidden p-[2px] w-[20px] h-[20px] md:w-[22px] md:h-[22px]":
                hole.grossScore && hole.grossScore < hole.par - 1,
              //bogey
              "bg-tm-c-dark":
                hole.grossScore && hole.grossScore === hole.par + 1,
              //dbogey
              "border border-tm-c-dark overflow-hidden p-[2px] w-[20px] h-[20px] md:w-[22px] md:h-[22px]":
                hole.grossScore && hole.grossScore > hole.par + 1,
            }
          );

          return (
            <div
              className={clsx(
                `flex flex-col justify-between text-center w-full cursor-pointer transition-colors duration-300 hover:bg-[rgba(236,105,26,0.4)]
                last-of-type:hover:bg-transparent last-of-type:hover:cursor-default
                [&>*]:flex [&>*]:items-center [&>*]:justify-center
                [&>*]:min-h-8 [&>*]:first-child:h-[40px] md:[&>*]:min-h-[40px] md:[&>*]:first-child:h-[56px] lg:[&>*]:min-h-[44px]
                [&>*]:last-of-type:bg-[rgba(118,118,118,0.5)]
                `,
                {
                  "bg-[rgba(236,105,26,0.8)]":
                    currentHoleNumber === hole.holeNumber,
                }
              )}
              onClick={() => {
                handleHoleClick({ holeNumber: hole.holeNumber });
              }}
              key={hole.holeNumber}
            >
              <div className="flex flex-col gap-[5px] justify-start pt-2.5 [&>*]:leading-[1]">
                <span>{hole.holeNumber}</span>
                {hole?.shots?.find((shot: { videos: [] }) => {
                  return !!shot?.videos?.length;
                }) && <PlayCircleOutlineIcon className="text-xs md:text-xl" />}
              </div>
              <div>{Math.floor(hole.distance * 1.09361)}</div>
              <div>{hole.par}</div>
              <div>
                <span className={renderLegendClasses}>
                  <span
                    className={clsx(
                      "flex items-center justify-center w-full h-full leading-[1] m-0 p-0",
                      {
                        "bg-tm-c-brand rounded-full flex items-center justify-center w-full h-full leading-[1] md:leading-[18px]":
                          hole.grossScore && hole.grossScore < hole.par - 1,
                        "bg-tm-c-dark flex items-center justify-center w-full h-full leading-[1]":
                          hole.grossScore && hole.grossScore > hole.par + 1,
                      }
                    )}
                  >
                    {hole.grossScore || "-"}
                  </span>
                </span>
              </div>
            </div>
          );
        })}
      <div
        className={`
          flex flex-col justify-between text-center w-full transition-colors duration-300
          last-of-type:hover:bg-transparent last-of-type:hover:cursor-default [&>*]:last-of-type:bg-[rgba(118,118,118,0.5)]
          [&>*]:flex [&>*]:items-center [&>*]:justify-center
          [&>*]:min-h-8 [&>*]:first-child:h-[40px] md:[&>*]:min-h-[40px] md:[&>*]:first-child:h-[56px] lg:[&>*]:min-h-[44px]
          cursor-default bg-[rgba(118,118,118,0.5)] hover:bg-[rgba(118,118,118,0.5)]
        `}
      >
        {!isLoading && <div>{isFront ? "Out" : "In"}</div>}
        <div>
          {isLoading ? (
            <span className="inline-block h-[15px] w-[20px] bg-tm-c-gray-950 opacity-60 rounded-[10px]" />
          ) : (
            yards
          )}
        </div>
        <div>
          {isLoading ? (
            <span className="inline-block h-[15px] w-[20px] bg-tm-c-gray-950 opacity-60 rounded-[10px]" />
          ) : (
            par
          )}
        </div>
        <div>
          {isLoading ? (
            <span className="inline-block h-[15px] w-[20px] bg-tm-c-gray-950 opacity-60 rounded-[10px]" />
          ) : (
            score
          )}
        </div>
      </div>
      {isFront && !isNineHoleRound && (
        <div
          className={`
            flex flex-col justify-between text-center w-full cursor-pointer transition-colors duration-300 hover:bg-[rgba(236,105,26,0.4)]
            last-of-type:hover:bg-transparent last-of-type:hover:cursor-default
            [&>*]:flex [&>*]:items-center [&>*]:justify-center
            [&>*]:min-h-[32px] [&>*]:first-child:h-[40px] md:[&>*]:min-h-[40px] md:[&>*]:first-child:h-[56px] lg:[&>*]:min-h-[44px]
            [&>*]:last-of-type:bg-[rgba(118,118,118,0.5)]
            [&>span]:opacity-0 min-[1311px]:hidden
          `}
        >
          <div />
          <div />
          <div />
          <div>
            <span>-</span>
          </div>
        </div>
      )}
      {(!isFront || isNineHoleRound) && (
        <div
          className={`
            flex flex-col justify-between text-center w-full cursor-pointer transition-colors duration-300 hover:bg-[rgba(236,105,26,0.4)]
            last-of-type:hover:bg-transparent last-of-type:hover:cursor-default
            [&>*]:flex [&>*]:items-center [&>*]:justify-center
            [&>*]:min-h-[32px] [&>*]:first-child:h-[40px] md:[&>*]:min-h-[40px] md:[&>*]:first-child:h-[56px] lg:[&>*]:min-h-[44px]
            [&>*]:last-of-type:bg-[rgba(118,118,118,0.5)]
          `}
        >
          {!isLoading && <div>Total</div>}
          <div>
            {isLoading ? (
              <span className="inline-block h-[15px] w-[20px] bg-tm-c-gray-950 opacity-60 rounded-[10px]" />
            ) : (
              totalYards
            )}
          </div>
          <div>
            {isLoading ? (
              <span className="inline-block h-[15px] w-[20px] bg-tm-c-gray-950 opacity-60 rounded-[10px]" />
            ) : (
              totalPar
            )}
          </div>
          <div>
            {isLoading ? (
              <span className="inline-block h-[15px] w-[20px] bg-tm-c-gray-950 opacity-60 rounded-[10px]" />
            ) : (
              totalScore
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScorecardDataTable;
